/**
 * The overlap between two parts at a take: preroll, transitions, postroll and
 * autoNext overlap (PLAN §5.1's `inTransition`, `outTransition`,
 * `autoNextOverlap`, piece `prerollDuration` / `postrollDuration`).
 *
 * Ported from Sofie's `calculatePartTimings`
 * (sofie-core `packages/corelib/src/playout/timings.ts`, MIT, © NRK), minus
 * what ACP v1 does not have: hold, transition *pieces* (Sofie's
 * `IBlueprintPieceType.InTransition` — our `inTransition` is timing only, so
 * `inTransitionStart` is reported but nothing is placed at it) and
 * `disableNextInTransition`. Every number is relative to the start of the part
 * group being taken; nothing is negative.
 */
import type { PartFields } from '@acp/domain/fields.js';
import { z } from 'zod';

export const partCalculatedTimingsSchema = z
  .object({
    /** Where in the new part group its in-transition starts, or null with no transition. */
    inTransitionStart: z.number().nonnegative().nullable(),
    /** How long after the part group's start piece time 0 is. */
    toPartDelay: z.number().nonnegative(),
    toPartPostroll: z.number().nonnegative(),
    /** How long after the new part group's start the previous part group keeps running. */
    fromPartRemaining: z.number().nonnegative(),
    fromPartPostroll: z.number().nonnegative(),
    fromPartKeepalive: z.number().nonnegative(),
  })
  .strict();
export type PartCalculatedTimings = z.infer<typeof partCalculatedTimingsSchema>;

/** What the calculation reads of a piece. */
export type TimingPiece = {
  readonly enable: { readonly start: number | 'now'; readonly duration: number | null };
  readonly prerollDuration: number;
  readonly postrollDuration: number;
};

export type TimingFromPart = Pick<PartFields, 'autoNext' | 'autoNextOverlap' | 'outTransition'>;
export type TimingToPart = Pick<PartFields, 'inTransition'>;

/** How far before the part the pieces protrude. A piece starting `'now'` was adlibbed and does not count. */
function partPreroll(pieces: readonly TimingPiece[]): number {
  let preroll = 0;
  for (const piece of pieces) {
    if (piece.enable.start === 'now' || piece.prerollDuration === 0) continue;
    preroll = Math.max(preroll, piece.prerollDuration - piece.enable.start);
  }
  return preroll;
}

/** The longest postroll of a piece that runs to the part's end (a piece with a duration presumably ends first). */
function partPostroll(pieces: readonly TimingPiece[]): number {
  let postroll = 0;
  for (const piece of pieces) {
    if (piece.postrollDuration === 0 || piece.enable.duration !== null) continue;
    postroll = Math.max(postroll, piece.postrollDuration);
  }
  return postroll;
}

type Transition = { readonly contentDelay: number; readonly previousPartKeepalive: number };

/**
 * The timings for taking `toPart` while `fromPart` plays (null: the first take
 * of an activation).
 */
export function calculatePartTimings(
  fromPart: TimingFromPart | null,
  fromPieces: readonly TimingPiece[],
  toPart: TimingToPart,
  toPieces: readonly TimingPiece[],
): PartCalculatedTimings {
  const toPartPreroll = partPreroll(toPieces);
  const fromPartPostroll = fromPart === null ? 0 : partPostroll(fromPieces);
  const toPartPostroll = partPostroll(toPieces);

  let transition: Transition | null = null;
  let allowTransitionPiece = false;
  if (fromPart !== null) {
    if (fromPart.autoNext && fromPart.autoNextOverlap > 0) {
      // "An auto-next with overlap is essentially a simple transition, so we treat it as one" (Sofie).
      transition = { contentDelay: 0, previousPartKeepalive: fromPart.autoNextOverlap };
    } else if (toPart.inTransition !== null) {
      allowTransitionPiece = true;
      transition = { contentDelay: toPart.inTransition.contentDelay, previousPartKeepalive: toPart.inTransition.previousPartKeepalive };
    }
  }

  if (transition === null || fromPart === null) {
    // Delay the new part so the old part's out-transition completes and the new part's prerolls fit.
    const takeOffset = Math.max(0, fromPart?.outTransition?.duration ?? 0, toPartPreroll);
    return {
      inTransitionStart: null,
      toPartDelay: takeOffset,
      toPartPostroll,
      fromPartRemaining: takeOffset + fromPartPostroll,
      fromPartPostroll,
      fromPartKeepalive: 0,
    };
  }
  const outTransitionTime = fromPart.outTransition === null ? 0 : fromPart.outTransition.duration - transition.previousPartKeepalive;
  const prerollTime = toPartPreroll - transition.contentDelay;
  const takeOffset = Math.max(0, outTransitionTime, prerollTime);
  return {
    inTransitionStart: allowTransitionPiece ? takeOffset : null,
    toPartDelay: takeOffset + transition.contentDelay,
    toPartPostroll,
    fromPartRemaining: takeOffset + transition.previousPartKeepalive + fromPartPostroll,
    fromPartPostroll,
    fromPartKeepalive: transition.previousPartKeepalive,
  };
}
